import type {ApiWorkforceProfileRecord} from '../WorkforceProfiles/workforceProfileApiTypes';

import {asApiWorkforceProfileRecord} from './apiRecordUtils';
import {mapLocalizedTextField} from './mapRequiredLanguageProfile';
import type {
  CareerCoachAnalysis,
  CareerCoachDevelopmentPlan,
  CareerCoachEmergingSkill,
  CareerCoachFutureProofing,
  CareerCoachMarketTrend,
  CareerCoachMilestone,
  CareerCoachPriorityGap,
  CareerCoachPriorityGapType,
  CareerCoachRecommendedAction,
  CareerCoachResource,
} from '../../types/WorkforceProfileTypes';


const mapCareerCoachResource = (raw: ApiWorkforceProfileRecord | undefined): CareerCoachResource | null => {
  if (!raw || !raw.title) return null;
  return {
    title: String(raw.title),
    type: (raw.type as CareerCoachResource['type']) ?? 'practice',
    authorOrProvider: (raw.authorOrProvider as string | undefined)?.trim() || null,
  };
};

const mapCareerCoachRecommendedActions = (raw: unknown): CareerCoachRecommendedAction[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const item = asApiWorkforceProfileRecord(entry);
    const timelineRaw = item.timeline;
    const timeline = timelineRaw == null ? null : mapLocalizedTextField(timelineRaw);

    return {
      action: mapLocalizedTextField(item.action),
      resource: mapCareerCoachResource(item.resource as ApiWorkforceProfileRecord | undefined),
      timeline: timeline?.en || timeline?.de ? timeline : null,
    };
  });
};

const mapCareerCoachPriorityGaps = (raw: unknown): CareerCoachPriorityGap[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const item = asApiWorkforceProfileRecord(entry);
    return {
      skill: mapLocalizedTextField(item.skill),
      type: (item.type as CareerCoachPriorityGapType) ?? 'core_competency',
      currentEvidence: mapLocalizedTextField(item.currentEvidence),
      targetExpectation: mapLocalizedTextField(item.targetExpectation),
      recommendedActions: mapCareerCoachRecommendedActions(item.recommendedActions),
    };
  });
};

const mapCareerCoachMilestones = (raw: unknown): CareerCoachMilestone[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const item = asApiWorkforceProfileRecord(entry);
    return {
      month: Number(item.month ?? 0),
      goal: mapLocalizedTextField(item.goal),
    };
  });
};

const mapCareerCoachDevelopmentPlan = (
  raw: ApiWorkforceProfileRecord | undefined
): CareerCoachDevelopmentPlan | null => {
  if (!raw) return null;
  return {
    summary: mapLocalizedTextField(raw.summary),
    priorityGaps: mapCareerCoachPriorityGaps(raw.priorityGaps),
    milestones: mapCareerCoachMilestones(raw.milestones),
  };
};

const mapCareerCoachMarketTrends = (raw: unknown): CareerCoachMarketTrend[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const item = asApiWorkforceProfileRecord(entry);
    return {
      trend: mapLocalizedTextField(item.trend),
      relevance: mapLocalizedTextField(item.relevance),
      recommendation: mapLocalizedTextField(item.recommendation),
    };
  });
};

const mapCareerCoachEmergingSkills = (raw: unknown): CareerCoachEmergingSkill[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const item = asApiWorkforceProfileRecord(entry);
    return {
      skill: mapLocalizedTextField(item.skill),
      rationale: mapLocalizedTextField(item.rationale),
      resource: mapCareerCoachResource(item.resource as ApiWorkforceProfileRecord | undefined),
    };
  });
};

const mapCareerCoachFutureProofing = (raw: ApiWorkforceProfileRecord | undefined): CareerCoachFutureProofing | null => {
  if (!raw) return null;
  return {
    summary: mapLocalizedTextField(raw.summary),
    marketTrends: mapCareerCoachMarketTrends(raw.marketTrends),
    emergingSkills: mapCareerCoachEmergingSkills(raw.emergingSkills),
  };
};

export const mapCareerCoachAnalysis = (
  raw: ApiWorkforceProfileRecord | undefined
): CareerCoachAnalysis | undefined => {
  if (!raw || Object.keys(raw).length === 0) return undefined;

  if (raw.available === false) {
    return {
      available: false,
      reason: (raw.reason as string | undefined) ?? null,
    };
  }

  return {
    available: true,
    developmentPlan: mapCareerCoachDevelopmentPlan(raw.developmentPlan as ApiWorkforceProfileRecord | undefined),
    futureProofing: mapCareerCoachFutureProofing(raw.futureProofing as ApiWorkforceProfileRecord | undefined),
  };
};
